import React, { useState, useCallback } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { useRowLinkBuilder } from '../../../lib/useRowLink';
import type { ColumnDef, StockFeatures } from '@tanstack/react-table';
import {
  Page as PageLayout,
  DataTable,
  EmptyState,
  LoadingOrbit,
  Text,
  Button,
  TagBadge,
  badgeVariantToTagColor,
} from '@chatsdet/ui';
import { Sparkle, Play } from '@phosphor-icons/react';
import { useBreadcrumbs } from '../../../contexts/BreadcrumbsContext';
import {
  useExploratoryTests,
  StartExploratoryTestDialog,
  getExploratoryTestStatusMeta,
} from '../../../features/exploratory-tests';
import type {
  ExploratoryTestListItem,
  ExploratoryTestStatus,
} from '../../../features/exploratory-tests';
import { ExploratoryTestNameCell } from './ExploratoryTestsPage';

/** Flat letter grade from the stored 0–100 overall score. */
function scoreToLetter(score: number | null | undefined): string {
  if (score === null || score === undefined) return '—';
  if (score >= 90) return 'A';
  if (score >= 80) return 'B';
  if (score >= 70) return 'C';
  if (score >= 60) return 'D';
  return 'F';
}

function gradeTone(letter: string): string {
  if (letter === 'A') return 'text-emerald-600';
  if (letter === 'B') return 'text-lime-600';
  if (letter === 'C') return 'text-amber-600';
  if (letter === 'D') return 'text-orange-600';
  if (letter === 'F') return 'text-red-600';
  return 'text-muted-foreground';
}

const formatDuration = (durationMs: number | null | undefined): string => {
  if (!durationMs) return '-';
  if (durationMs < 1000) return '<1s';
  if (durationMs < 60000) return `${Math.round(durationMs / 1000)}s`;
  if (durationMs < 3600000) return `${Math.round(durationMs / 60000)}m`;
  return `${Math.round(durationMs / 3600000)}h`;
};

/**
 * Labs › UX Review — the persona-walkthrough flavor of the exploratory
 * primitive. Reuses the exploratory list data + launcher + detail page, scoped
 * to `flavor: 'ux'` (the flag cuts through the shared UI).
 */
export function UxReviewPage() {
  const navigate = useNavigate();
  const buildHref = useRowLinkBuilder();
  const search = useSearch({ from: '/runs/ux-review' }) as { page: number; pageSize: number };
  const page = search.page;
  const pageSize = search.pageSize;
  const [dialogOpen, setDialogOpen] = useState(false);

  useBreadcrumbs([{ key: 'ux-review', label: 'UX Review' }]);

  const handlePageChange = useCallback(
    (nextPage: number) => {
      navigate({
        to: '/runs/ux-review',
        search: (prev: Record<string, unknown>) => {
          const next: Record<string, unknown> = { ...prev, page: nextPage };
          if (nextPage === 1) delete next.page;
          return next;
        },
      });
    },
    [navigate]
  );

  const testsQuery = useExploratoryTests({ page, pageSize, flavor: 'ux' });

  const newButton = (size: 'sm' | 'md') => (
    <Button
      size={size}
      variant="primary"
      iconLeft={<Play size={size === 'sm' ? 14 : 16} weight="fill" />}
      onPress={() => setDialogOpen(true)}
    >
      New UX review
    </Button>
  );

  const columns: ColumnDef<StockFeatures, ExploratoryTestListItem>[] = React.useMemo(
    () => [
      {
        accessorKey: 'status',
        header: 'Status',
        cell: ({ row }) => {
          const meta = getExploratoryTestStatusMeta(row.original.status as ExploratoryTestStatus);
          return (
            <TagBadge name={meta.label} color={badgeVariantToTagColor(meta.badgeVariant)} hideDot />
          );
        },
        meta: { flex: 0.8 },
      },
      {
        id: 'name',
        header: 'Persona & goal',
        cell: ({ row }) => <ExploratoryTestNameCell test={row.original} />,
        meta: { flex: 3 },
      },
      {
        accessorKey: 'propertyName',
        header: 'Application',
        cell: ({ row }) => (
          <Text size="sm" className="truncate text-muted-foreground">
            {row.original.propertyName}
          </Text>
        ),
        meta: { flex: 1.5 },
      },
      {
        id: 'grade',
        header: 'Grade',
        cell: ({ row }) => {
          const letter = scoreToLetter(row.original.overallScore);
          return (
            <Text size="sm" weight="bold" className={gradeTone(letter)}>
              {letter}
            </Text>
          );
        },
        meta: { flex: 0.6 },
      },
      {
        accessorKey: 'issuesFound',
        header: 'Findings',
        cell: ({ row }) => (
          <Text size="sm" className="text-muted-foreground">
            {row.original.issuesFound}
          </Text>
        ),
        meta: { flex: 0.7 },
      },
      {
        id: 'duration',
        header: 'Duration',
        cell: ({ row }) => (
          <Text size="sm" className="text-muted-foreground">
            {formatDuration(row.original.durationMs)}
          </Text>
        ),
        meta: { flex: 0.8 },
      },
      {
        accessorKey: 'createdAt',
        header: 'Started',
        cell: ({ row }) => (
          <Text size="sm" className="text-muted-foreground">
            {row.original.startedAt
              ? new Date(row.original.startedAt).toLocaleString()
              : new Date(row.original.createdAt).toLocaleString()}
          </Text>
        ),
        meta: { flex: 1.5 },
      },
    ],
    []
  );

  return (
    <PageLayout>
      {testsQuery.isLoading ? (
        <div className="flex items-center justify-center py-12">
          <LoadingOrbit size="lg" />
        </div>
      ) : testsQuery.data && testsQuery.data.items.length > 0 ? (
        <>
          <div className="mb-4 flex justify-end">{newButton('md')}</div>
          <DataTable
            columns={columns}
            data={testsQuery.data.items}
            aria-label="UX reviews table"
            serverPagination={{
              page,
              pageSize,
              pageCount: testsQuery.data.pagination.totalPages,
              totalItems: testsQuery.data.pagination.totalItems,
              onPageChange: handlePageChange,
            }}
            getRowHref={(test) =>
              buildHref({
                to: '/runs/ux-review/$exploratoryTestId',
                params: { exploratoryTestId: test.id },
              })
            }
          />
        </>
      ) : (
        <EmptyState
          icon={<Sparkle size={48} />}
          title="No UX reviews yet"
          description="A UX review sends an AI persona through your app to accomplish a goal, then grades the experience and files findings. Describe who the user is and what they want."
          action={newButton('sm')}
        />
      )}

      {dialogOpen && (
        <StartExploratoryTestDialog
          isOpen={dialogOpen}
          onOpenChange={setDialogOpen}
          defaultFlavor="ux"
          lockFlavor
        />
      )}
    </PageLayout>
  );
}
